/**
 * Reconciles the active workspace and the URL, in both directions.
 *
 * The product shell (`_app`) has no workspace id in its route paths, so "which
 * workspace am I in" lives in `active-workspace.store` (persisted) and is mirrored
 * into the root `?ws=` search param. This component owns that mirror. It runs in
 * two directions, with a clear precedence at load:
 *
 * **URL → store, once, at load (JUI-55).** A shared link encodes the workspace in
 * `?ws=`. When a tab *opens* on one, that id must beat whatever this browser last
 * persisted — otherwise the persisted workspace silently overrides the link. We
 * capture the id the tab was opened with (before the outbound mirror below can
 * overwrite it) and, once auth + the caller's membership list resolve, adopt it
 * through `applyWorkspaceChange` (a real switch: mark + purge the previous
 * workspace's cache) and, when it moves the caller off a workspace they already
 * had, a warning toast announces the change ("Switching workspace from A to B") —
 * because unlike a rail switch, the user did not initiate this one. A `?ws=` the
 * caller cannot reach is treated as inert and dropped: the viewer still has
 * access to their own workspaces, so we land them on `/dashboard` under their
 * persisted/default workspace instead of showing a "membership removed" wall
 * for a workspace they were never a member of. This runs exactly once, latched
 * to the opened URL, so it never fights a later in-app rail switch.
 *
 * **store → URL, continuously.** Root search middleware retains `ws` once it
 * exists; this closes the direct-load gap by replacing the current URL with the
 * active workspace when resolution adopts a persisted/default value, without
 * disturbing route-local search state. The account console's `/w/$workspaceId/...`
 * routes carry their own canonical workspace identity, so this removes a redundant
 * `?ws=` from those URLs. `/workspaces` remains a neutral picker entry point. The
 * outbound mirror is held until the inbound adoption resolves so it cannot clobber
 * the opened `?ws=`.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'

import { useMyWorkspacesSummary } from '@/api/queries/acc-mgmt'
import { useToastStore } from '@/components/ui/toast/toast'
import {
  selectActiveWorkspaceIdFor,
  useActiveWorkspaceStore,
} from '@/stores/active-workspace.store'
import { useAuthStore } from '@/stores/auth.store'
import { applyWorkspaceChange } from './apply-workspace-change'

const PUBLIC_ARTIFACT_PATH = /^\/public\/artifacts\/[^/]+\/?$/

export function WorkspaceSearchSync() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  // Public artifacts are independent of the viewer's active workspace. Keep the
  // sync child unmounted there so it cannot fetch memberships, adopt `?ws=`, or
  // add the active workspace to the shared URL. Unmounting also discards a
  // pending product-route check if navigation reaches an artifact before it ends.
  if (PUBLIC_ARTIFACT_PATH.test(pathname)) return null

  return <WorkspaceSearchSyncForOtherRoutes />
}

function WorkspaceSearchSyncForOtherRoutes() {
  const location = useRouterState({ select: (state) => state.location })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const userId = useAuthStore((state) => state.user?.uid ?? null)
  const activeWorkspaceId = useActiveWorkspaceStore((state) =>
    selectActiveWorkspaceIdFor(state, userId),
  )
  const searchWorkspaceId =
    typeof location.search.ws === 'string' && location.search.ws.length > 0
      ? location.search.ws
      : undefined
  const routeCarriesWorkspaceId = /^\/w\/[^/]+(?:\/|$)/.test(location.pathname)

  // The workspace the tab was *opened* pointing at, captured once on the first
  // render — before the outbound mirror can rewrite the URL. Console routes carry
  // their own workspace identity (and their own access gate), and `/workspaces` is
  // a neutral picker, so inbound adoption only applies to product routes.
  const opened = useRef<{
    captured: boolean
    ws: string | null
    onProductRoute: boolean
    priorId: string | null
    priorOwner: string | null
  }>({
    captured: false,
    ws: null,
    onProductRoute: false,
    priorId: null,
    priorOwner: null,
  })
  if (!opened.current.captured) {
    // Read the *rehydrated* persisted choice synchronously, before any resolver
    // adopts a default — this is the genuine "from" for the switch toast, immune
    // to a fresh-session default landing in the store between renders.
    const persisted = useActiveWorkspaceStore.getState()
    opened.current = {
      captured: true,
      ws: searchWorkspaceId ?? null,
      onProductRoute: !routeCarriesWorkspaceId && location.pathname !== '/workspaces',
      priorId: persisted.activeWorkspaceId,
      priorOwner: persisted.ownerUserId,
    }
  }
  const wantsInbound = opened.current.ws !== null && opened.current.onProductRoute
  const [inboundPending, setInboundPending] = useState(wantsInbound)

  // Only fetch the membership list when this component actually needs it to adopt
  // an opened `?ws=` — never on unauthenticated pages. Shares the cache/key with
  // the `_app` consumer of `useActiveWorkspace`, so it does not double-fetch.
  const summary = useMyWorkspacesSummary({ enabled: !!userId && inboundPending })
  const inboundWorkspaces = summary.data?.workspaces

  // URL → store (load-time, one-shot).
  useEffect(() => {
    if (!inboundPending) return
    const target = opened.current.ws
    if (!target || !opened.current.onProductRoute) {
      setInboundPending(false)
      return
    }
    if (!userId) return // wait for auth before we can check membership / adopt
    if (summary.isError) {
      // Can't verify access — release the latch and let the persisted workspace
      // resolve, rather than stranding the outbound mirror behind a failed read.
      setInboundPending(false)
      return
    }
    if (!summary.isSuccess || !inboundWorkspaces) return // membership list still loading

    const targetWorkspace = inboundWorkspaces.find((w) => w.workspace_id === target)
    if (!targetWorkspace) {
      setInboundPending(false)
      // A ?ws= pointing at a workspace this user isn't a member of is treated
      // as inert: the viewer still has access to their own workspaces, so we
      // drop the opaque param and land them on their dashboard in full mode
      // rather than stranding them on /forbidden with "membership removed"
      // copy that doesn't apply (they were never a member). `gm` is dropped
      // alongside `ws` because the restricted shared URL that carried both
      // was not meant for this viewer.
      void navigate({
        to: '/dashboard',
        replace: true,
        search: (prev) => ({ ...prev, ws: undefined, gm: undefined }),
      })
      return
    }
    if (target !== activeWorkspaceId) {
      // The shared link moved the caller off the workspace they last had. Unlike a
      // rail switch (an explicit action), this one they did not initiate — announce
      // it so the context change isn't silent. Only when there is a genuine prior
      // choice of their own to switch *from*.
      const priorId =
        opened.current.priorOwner === userId ? opened.current.priorId : null
      if (priorId && priorId !== target) {
        const fromName = inboundWorkspaces.find((w) => w.workspace_id === priorId)?.name
        const toName = <span className="font-semibold">{targetWorkspace.name}</span>
        useToastStore
          .getState()
          .show(
            fromName ? (
              <>
                Switching workspace from <span className="font-semibold">{fromName}</span> to{' '}
                {toName}
              </>
            ) : (
              <>Switching to workspace {toName}</>
            ),
            'warning',
          )
      }
      void applyWorkspaceChange(queryClient, target)
    }
    setInboundPending(false)
  }, [
    inboundPending,
    userId,
    summary.isError,
    summary.isSuccess,
    inboundWorkspaces,
    activeWorkspaceId,
    queryClient,
    navigate,
  ])

  // store → URL (held until inbound adoption resolves so it cannot clobber `?ws=`).
  useEffect(() => {
    if (inboundPending) return

    if (routeCarriesWorkspaceId) {
      if (!searchWorkspaceId) return
      const nextUrl = new URL(location.href, window.location.origin)
      nextUrl.searchParams.delete('ws')
      void navigate({
        href: `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`,
        replace: true,
      })
      return
    }

    if (
      location.pathname === '/workspaces' ||
      !activeWorkspaceId ||
      searchWorkspaceId === activeWorkspaceId
    ) {
      return
    }

    const nextUrl = new URL(location.href, window.location.origin)
    nextUrl.searchParams.set('ws', activeWorkspaceId)
    void navigate({
      href: `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`,
      replace: true,
    })
  }, [
    inboundPending,
    location.href,
    location.pathname,
    navigate,
    routeCarriesWorkspaceId,
    searchWorkspaceId,
    activeWorkspaceId,
  ])

  return null
}
